import { i18n } from 'i18next';
import { TFunction } from '../types/TFunction';

const initLocalTFunction = (
  locale: string,
  packageName: string,
  i18nInstance: i18n,
  localTFunctions: Map<string, TFunction>,
): TFunction => {
  const fixedT = i18nInstance.getFixedT(locale, packageName) as TFunction;

  if (!fixedT) {
    // There is not much we can do here since this means i18next itself
    // has failed. Likely this will not occur, but we should give a
    // breadcrumb anyway.
    throw new Error(
      `Could not get fixed \`t\` function for "${locale}". This may be an issue with i18next itself.`,
    );
  }

  localTFunctions.set(locale, fixedT);

  return fixedT;
};

/**
 * Gets a t function for a given locale from a passed collection, adding a new
 * instance if one does not already exist for the locale.
 * @param locale The locale the t function should be based upon.
 * @param packageName The name of the package the t function will draw its
 * resources from.
 * @param i18nInstance The previously initialized i18nInstance to use when
 * generating new t functions.
 * @param localTFunctions A mutable map of locales to t functions.
 * @returns The singleton t function for the given locale.
 */
export const getLocalTFunction = (
  locale: string,
  packageName: string,
  i18nInstance: i18n,
  localTFunctions: Map<string, TFunction>,
): TFunction => {
  const t = localTFunctions.get(locale);

  if (t) {
    return t;
  }

  return initLocalTFunction(locale, packageName, i18nInstance, localTFunctions);
};
